import * as Three from 'three';
import { parseWallChains } from '../core/parse-wall-chains';
import HelvetikerFont from 'three/examples/fonts/helvetiker_regular.typeface.json';
import { modelLoader } from '../utils/loaders';
import { parseObject } from './object';
import { Water } from 'three/examples/jsm/objects/Water2';
import { loadTexture } from '../utils/loaders';
import waterNormalA from '../images/water/normal-a.jpg';
import waterNormalB from '../images/water/normal-b.jpg';

const generateLightNoise = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');

  for (let i = 0; i < 20000; i++) {
    ctx.fillStyle = 'hsl(0,0%,' + ( Math.random() * 50 + 50 ) + '%)';
    ctx.beginPath();
    ctx.arc( Math.random() * canvas.width, Math.random() * canvas.height, Math.random() + 0.15, 0, Math.PI * 2, true);
    ctx.fill();
  }

  ctx.globalAlpha = 0.075;
  ctx.globalCompositeOperation = 'lighter';

  return canvas;
}

export const parseFloor = (branch, context, options) => {
  const { _selfKey, parent } = options;
  const {
    yOffset,
    gridColor = 0xffffff,
    width = 100,
    height = 100,
    tintColor,
    materialId = 'clear'
  } = branch;
  const { renderQuality, currentFloorIndex, renderWalls } = context;
  let instance = parent.getObjectByName(_selfKey);
  const floorTintColor = new Three.Color(tintColor).getHSL();
  const isActiveFloor = currentFloorIndex === yOffset;

  if (!instance) {
    instance = new Three.Object3D();

    const gridSize = Math.max(width, height);

    const grid = new Three.GridHelper(gridSize, gridSize / 10, gridColor, gridColor);
    grid.name = '_ca-grid';
    instance.add(grid);
    grid.position.y = 1.0;

    if (yOffset === 0) {
      const groundGeometry = new Three.BoxBufferGeometry(width, 1, height, 1, 1, 1);
      let ground;

      if (materialId === 'grass') {
        const groundMaterial = new Three.MeshPhysicalMaterial({
          color: new Three.Color().setHSL(tintColor ? floorTintColor.h : 0.3, 0.3, 0.05),
          clearcoat: 0.0,
          clearcoatRoughness: 1.0,
          metalness: 0.0,
          roughness: 1.0
        });
        ground = new Three.Mesh(groundGeometry, groundMaterial);

        const grassGroup = new Three.Group();
        grassGroup.name = '_ca-ground-grass';
        const grassGeometry = new Three.PlaneBufferGeometry(width, height, 1, 1);
        const grassTexture = new Three.CanvasTexture(generateLightNoise());
        grassTexture.repeat.set(width / 20, height / 20);
        grassTexture.wrapS = Three.MirroredRepeatWrapping;
        grassTexture.wrapT = Three.MirroredRepeatWrapping;
        const grassTilt = -Math.PI / 2;

        for (let i = 0; i < 15; i++) {
          const grassMaterial = new Three.MeshBasicMaterial({
            color: new Three.Color().setHSL(tintColor ? floorTintColor.h : 0.3, 0.3, 0.1 + 0.4 * (i / 15)),
            map: grassTexture,
            depthWrite: false,
            transparent: true,
            side: Three.DoubleSide
          });
          const grass = new Three.Mesh(grassGeometry, grassMaterial);
          grass.receiveShadow = true;
          grass.castShadow = false;
          grass.rotation.x = grassTilt;
          grass.position.y = i * 0.15;
          grass.position.x += Math.random() * 0.1;
          grass.position.z += Math.random() * 0.1;
          grassGroup.add(grass);
        }

        instance.add(grassGroup);
      } else if (materialId === 'empty') {
        const groundMaterial = new Three.MeshPhysicalMaterial({
          color: new Three.Color(0xffffff),
          metalness: 0.0,
          roughness: 1.0,
          opacity: 0,
          transparent: true
        });
        ground = new Three.Mesh(groundGeometry, groundMaterial);
      } else {
        const groundMaterial = new Three.MeshPhysicalMaterial({
          color: new Three.Color(tintColor),
          clearcoat: 0.0,
          clearcoatRoughness: 1.0,
          metalness: 0.0,
          roughness: 1.0
        });
        ground = new Three.Mesh(groundGeometry, groundMaterial);
      }

      ground.receiveShadow = true;
      ground.castShadow = true;
      ground.name = '_ca-ground-mesh';
      instance.add(ground);
    } else {
      const groundGeometry = new Three.PlaneBufferGeometry(width, height, 1, 1);
      const groundMaterial = new Three.MeshBasicMaterial({
        color: 0xffffff,
        opacity: .1,
        transparent: true
      });
      const ground = new Three.Mesh(groundGeometry, groundMaterial);

      ground.rotation.x = -Math.PI / 2;
      ground.receiveShadow = false;
      ground.castShadow = false;
      ground.name = '_ca-ground-mesh';
      instance.add(ground);
    }

    parent.add(instance);

    branch._dirty = 2;
  }

  instance.position.y = yOffset * 25;

  const gridInstance = instance.getObjectByName('_ca-grid');
  const groundInstance = instance.getObjectByName('_ca-ground-mesh');

  if (gridInstance) {
    gridInstance.visible = isActiveFloor && renderQuality < 2;
  }

  if (groundInstance && yOffset > 0) {
    groundInstance.visible = renderQuality < 2 ? isActiveFloor : false;
  }

  if (currentFloorIndex < yOffset && renderWalls !== 2) {
    instance.visible = false;
  } else {
    instance.visible = true;
  }

  return instance;
};

const getSizeLabel = (length, context = {}) => {
  const { measurementUnits } = context;
  const scaledLength = length / 10 + 1.0;

  let value;

  if (measurementUnits === 'imperial') {
    value = scaledLength * 3.2808;
  } else {
    value = scaledLength;
  }

  const title = `${(value.toFixed(1))}${({
    'metric': 'm',
    'imperial': 'ft',
  })[measurementUnits] || ''}`;

  return title.replace('.', ',');
};

export const postRenderFloor = (instance, branch, context, options = {}) => {
  const { yOffset } = branch;
  const { renderWalls, renderMeasurements, currentFloorIndex, renderQuality } = context;
  const isActiveFloor = currentFloorIndex === yOffset;
  const { waterTiles } = options;

  if (branch._dirty !== 2) {
    return;
  }

  let water = instance.getObjectByName('_ca-water');

  if (water) {
    water.parent.remove(water);
  }

  const waterShapes = [];

  if (waterTiles && waterTiles.length >= 1) {
    waterTiles.forEach(({ positionX, positionZ }) => {
      const waterShape = new Three.Shape();
      waterShape.moveTo(positionX - 5, -positionZ - 5);
      waterShape.lineTo(positionX + 5, -positionZ - 5);
      waterShape.lineTo(positionX + 5, -positionZ + 5);
      waterShape.lineTo(positionX - 5, -positionZ + 5);
      waterShape.lineTo(positionX - 5, -positionZ - 5);
      waterShapes.push(waterShape);
    });

    if (renderQuality === 2) {
      water = new Water(new Three.ShapeBufferGeometry(waterShapes), {
        color: '#6f89a3',
        scale: .01,
        normalMap0: loadTexture(waterNormalA),
        normalMap1: loadTexture(waterNormalB),
        reflectivity: 0.75,
        encoding: Three.sRGBEncoding,
        textureHeight: 256,
        textureWidth: 256
      });
    } else {
      water = new Three.Mesh(new Three.ShapeBufferGeometry(waterShapes), new Three.MeshStandardMaterial({
        color: '#6f89a3',
        roughness: .7,
        metalness: .1
      }));
    }
    water.name = '_ca-water';

    water.position.set(0, 2.1, 0);
    water.rotation.x = Math.PI * - 0.5;
    instance.add(water);
  }

  const wallChainsOffsetY = [
    5,
    25,
    25
  ][renderWalls] || 5;
  const oldWallChains = instance.getObjectByName('wall-chains');

  if (oldWallChains) {
    instance.remove(oldWallChains);
  }

  if (!isActiveFloor && renderWalls < 2) {
    return;
  }

  branch.wallChains = parseWallChains(branch) || {};
  
  const wallChains = new Three.Group();
  wallChains.name = 'wall-chains';
  wallChains.castShadow = false;
  wallChains.receiveShadow = false;
  instance.add(wallChains);

  // if (renderWalls < 2 && renderQuality < 2) {
    Object.keys(branch.wallChains.lines).forEach((id) => {
      const line = branch.wallChains.lines[id];
      const firstWall = line[0];
      const lastWall = line[line.length - 1];
      const startMean = new Three.Vector3(
        (firstWall.positionX + firstWall.endX) / 2,
        0,
        (firstWall.positionZ + firstWall.endZ) / 2,
      );
      const endMean = new Three.Vector3(
        (lastWall.positionX + lastWall.endX) / 2,
        0,
        (lastWall.positionZ + lastWall.endZ) / 2,
      );
      const lineMean = new Three.Vector3(
        (startMean.x + endMean.x) / 2,
        0,
        (startMean.z + endMean.z) / 2,
      );
      let commonAngle = null;

      line.forEach(({ positionX, positionZ, endX, endZ, content }) => {
        if (renderWalls === 0 && content && content.length > 0) {
          return;
        }

        const width = endX - positionX;
        const depth = endZ - positionZ;

        if (commonAngle === null) {
          commonAngle = new Three.Vector3(width, 0, depth).angleTo(new Three.Vector3(1, 0, 0));
        }

        const geometry = new Three.BoxBufferGeometry(
          Math.max(Math.abs(width), Math.abs(depth)) + 1.5,
          1,
          1.5,
          1,
          1,
          1
        );
        const mesh = new Three.Mesh(geometry, new Three.MeshStandardMaterial({ color: 0x000000, metalness: 0, roughness: .5 }));
        mesh.translateX(positionX + width / 2);
        mesh.translateY(wallChainsOffsetY + .9);
        mesh.translateZ(positionZ + depth / 2);
        mesh.rotateY(commonAngle);
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        wallChains.add(mesh);
      });

      if (renderMeasurements) {
        modelLoader.font().load(HelvetikerFont, (font) => {
          const title = getSizeLabel(endMean.clone().sub(startMean).length(), context);
          const geometry = new Three.TextBufferGeometry(title, {
            font,
            size: 2,
            height: .1,
            curveSegments: 3,
            bevelEnabled: false
          });
          const mesh = new Three.Mesh(geometry, new Three.MeshBasicMaterial({ color: 0x000000 }));
          mesh.translateX(lineMean.x);
          mesh.translateZ(lineMean.z);
          mesh.translateY(wallChainsOffsetY + 3);
          mesh.rotateY((commonAngle % Math.PI) === 0 ? 0 : Math.PI / 2);
          mesh.translateX(-title.length * .5);
          wallChains.add(mesh);
        });
      }
    });
  // }

  const wallHeight = [
    5,
    25,
    25
  ][renderWalls] || 25;

  (branch.wallChains.junctions || []).forEach(junction => {
    const { wallA, wallB } = junction;
    const dirA = new Three.Vector3(wallA.endX - wallA.positionX, 0, wallA.endZ - wallA.positionZ);
    const dirB = new Three.Vector3(wallB.endX - wallB.positionX, 0, wallB.endZ - wallB.positionZ);
    const materials = [];

    const addMaterialMapping = (direction, sideA, sideB) => {
      let angle = direction.angleTo(new Three.Vector3(0, 0, 1));

      if (angle === 0) {
        materials[1] = sideA;
        materials[3] = sideB;
      } else if (angle === Math.PI) {
        materials[1] = sideB;
        materials[3] = sideA;
      } else {
        angle = direction.angleTo(new Three.Vector3(1, 0, 0));

        if (angle === 0) {
          materials[0] = sideB;
          materials[2] = sideA;
        } else {
          materials[0] = sideA;
          materials[2] = sideB;
        }
      }
    };
    addMaterialMapping(dirA, wallA.materialA, wallA.materialB);
    addMaterialMapping(dirB, wallB.materialA, wallB.materialB);

    for (let i = 0; i < 4; i++) {
      let junctionMark = parseObject({
        sizeX: 1,
        sizeZ: wallHeight,
        positionX: 0,
        positionY: (wallHeight / 2) + 0.5,
        positionZ: 0,
        primitive: 'plane',
        opacity: 1.0,
        materialId: materials[i] || 'brick-wall',
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
      }, context, {
        parent: wallChains,
        selectable: false,
        selectionTarget: false
      });
      junctionMark.translateX(junction.positionX);
      junctionMark.translateZ(junction.positionZ);
      junctionMark.rotateY(Math.PI / 4 * i);
      junctionMark.translateOnAxis(junctionMark.getWorldDirection(), .5);
      junctionMark.rotateY(Math.PI / 4 * i);
    }
  });
};
